import { useMutation, useQuery } from 'react-query';
import { httpRequest } from '../config/api';
import axios from 'axios';

export const reportService = {
  getLoadAllData: (periodId, params) => httpRequest.get(`api/report/${periodId}`, { params }),
  getReportErrors: (periodId, params) =>
    httpRequest.get(`api/report/errors/${periodId}`, { params }),
  getApproverReport: (periodId, params) =>
    httpRequest.get(`api/approver-report/${periodId}`, { params }),
  getAIFMReport: (periodId, params) => httpRequest.get(`api/report/aifm/${periodId}`, { params }),
  getJurisdictions: (periodId, params) =>
    httpRequest.get(`/api/report/get-jurisdictions/${periodId}`, { params }),
  getAIFMJurisdictions: (periodId, params) =>
    httpRequest.get(`/api/report/get-aifm-jurisdictions/${periodId}`, { params }),
  getReportSchema: (periodId, params) =>
    httpRequest.get(`api/report-schema/${periodId}`, { params }),
  reviewReport: ({ periodId, params }) =>
    httpRequest.post(`api/report/${periodId}/review`, {}, { params }),
  suggestReportReviewFix: ({ periodId, params, body }) =>
    httpRequest.post(`api/report/${periodId}/review/fix-suggestion`, body, { params }),
  getLatestReportReview: ({ periodId, params }) =>
    httpRequest.get(`api/report/${periodId}/review/latest`, { params }),
  getEsmaReportReviewRules: () => httpRequest.get('api/report-review/rules/esma'),
  getSourceData: params => httpRequest.get('api/drill-down/get-source-data', { params }),
  getDrillDown: data => httpRequest.post('api/drill-down/explain', data),
  getExportParams: id => httpRequest.get(`api/export-params/${id}`),
  getFCACredentials: (workflowId, params) =>
    httpRequest.get(`api/workflows/${workflowId}/retrieve-credentials/`, {
      params
    }),
  createXML: ({ data, periodId }) =>
    axios.post(
      `${import.meta.env.VITE_BASE_URL}api/report/create_xml_zip/${periodId}/`,
      data.body,
      {
        params: data.params,
        headers: {
          Accept: 'application/zip',
          'Response-Type': 'blob'
        }
      }
    ),
  createFCACredentials: ({ data, workflowId }) =>
    httpRequest.post(`api/workflows/${workflowId}/store-credentials/`, data),
  regenerateAIFMReport: data =>
    httpRequest.post(
      `api/report/aifm/force-update/${data.periodId}`,
      {},
      {
        params: data.params
      }
    ),
  createAIFMReport: data => httpRequest.post(`api/report/aifm/${data.id}`),
  replicateStaticData: data =>
    httpRequest.post(`api/report/${data.periodId}/replicate-template/${data.memberState}`),
  updateFCACredentials: ({ data, workflowId }) =>
    httpRequest.put(`api/workflows/${workflowId}/update-credentials/`, data),
  deleteFCACredentials: workflowId =>
    httpRequest.delete(`api/workflows/${workflowId}/delete-credentials/`),
  deleteStaticData: data =>
    httpRequest.delete(`api/report/static/${data.periodId}`, {
      data: data.body
    }),
  updateData: (data, periodId, params) =>
    httpRequest.put(`api/report/${periodId}/`, data, {
      params
    }),
  updateAIFMData: (data, periodId, params) =>
    httpRequest.put(`api/report/aifm/${periodId}/`, data, {
      params
    }),
  // Report overrides: per-period manual values kept apart from the generated rows.
  patchOverrides: ({ periodId, reportingMemberState, changes }) =>
    httpRequest.patch(`api/periods/${periodId}/reports/${reportingMemberState}/overrides`, {
      changes
    }),
  deleteOverrides: ({ periodId, reportingMemberState, fundIds }) =>
    httpRequest.delete(`api/periods/${periodId}/reports/${reportingMemberState}/overrides`, {
      params: fundIds?.length ? { fund_id: fundIds } : undefined,
      paramsSerializer: { indexes: null }
    }),
  forceUpdateReport: data =>
    httpRequest.post(`api/report/force-update/${data.periodId}`, data.body),
  invite: data =>
    httpRequest.post('api/invite', data.body, {
      params: data.params
    }),
  invitePending: data =>
    httpRequest.post('api/invite/pending/send', data.body, {
      params: data.params
    }),
  resendInvite: data =>
    httpRequest.post('api/invite/resend', null, {
      params: data.params
    }),
  deleteApprover: data =>
    httpRequest.delete('api/invite/pending/delete', {
      params: data.params
    }),
  approvalRequiredAIF: data =>
    httpRequest.post('api/approval-required/aif', data.body, {
      params: data.params
    }),
  approvalRequiredAIFM: data =>
    httpRequest.post('api/approval-required/aifm', data.body, {
      params: data.params
    }),
  accessFundChange: data =>
    httpRequest.post('api/access/fund/change', data.body, {
      params: data.params
    }),
  accessAllFundsChange: data =>
    httpRequest.post('api/access/funds/change', data.body, {
      params: data.params
    }),
  deleteInvite: id => httpRequest.delete(`api/invite/${id}`),
  inviteFromSettings: data => httpRequest.post('api/invite-from-settings', data.body),
  approve: data =>
    httpRequest.post('api/approve/fund', data.body, {
      params: data.params
    }),
  approveAIFM: data =>
    httpRequest.post('api/approve/aifm', data.body, {
      params: data.params
    }),
  approveAllFunds: data =>
    httpRequest.post('api/approve/funds-bulk', data.body, {
      params: data.params
    }),
  approvalStatus: params =>
    httpRequest.get('api/approval-fund-status', {
      params
    }),
  approvalAllFundsStatus: params =>
    httpRequest.get('api/approve/funds-bulk', {
      params
    }),
  approvalAIFMStatus: params =>
    httpRequest.get('api/approval-aifm-status', {
      params
    }),
  getCheckAIFOrAIFM: params => httpRequest.get('api/aifm-aif-check', { params }),
  submitReport: data => httpRequest.post('api/report/submit/', data),
  deactivateUser: data => httpRequest.put(`api/approvers/${data.id}/deactivate`)
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetLoadAllData = ({ periodId, params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_REPORT_BY_ID', params, periodId],
    () => reportService.getLoadAllData(periodId, params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetReportErrors = ({ periodId, params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_REPORT_ERRORS', params, periodId],
    () => reportService.getReportErrors(periodId, params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetApproverReport = ({ periodId, params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_APPROVER_REPORT_BY_ID', params, periodId],
    () => reportService.getApproverReport(periodId, params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useCheckAIFOrAIFM = ({ params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['CHECK_AIFM_OR_AIF', params],
    () => reportService.getCheckAIFOrAIFM(params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetAIFMReport = ({ periodId, params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_AIFM_REPORT_BY_ID', params, periodId],
    () => reportService.getAIFMReport(periodId, params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetJurisdictions = ({ periodId, params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['useGetJurisdictions', params, periodId],
    () => reportService.getJurisdictions(periodId, params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetAIFMJurisdictions = ({ periodId, params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['useGetAIFMJurisdictions', params, periodId],
    () => reportService.getAIFMJurisdictions(periodId, params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetSourceData = ({ params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['useGetSourceData', params],
    () => reportService.getSourceData(params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useExportParams = ({ id, queryParams = {} } = {}) => {
  return useQuery(['useExportParams', id], () => reportService.getExportParams(id), queryParams);
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetApprovalStatus = ({ params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['useGetApprovalStatus', params],
    () => reportService.approvalStatus(params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetApprovalAllFunds = ({ params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['useGetApprovalAllFunds', params],
    () => reportService.approvalAllFundsStatus(params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetApprovalAIFMStatus = ({ params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['useGetApprovalAIFMStatus', params],
    () => reportService.approvalAIFMStatus(params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetReportSchema = ({ params = {}, periodId, queryParams = {} } = {}) => {
  return useQuery(
    ['useGetReportSchema', params],
    () => reportService.getReportSchema(periodId, params),
    queryParams
  );
};

export const useReviewReport = () => {
  return useMutation(reportService.reviewReport);
};

export const useSuggestReportReviewFix = () => {
  return useMutation(reportService.suggestReportReviewFix);
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useLatestReportReview = ({ periodId, params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['useLatestReportReview', periodId, params],
    () => reportService.getLatestReportReview({ periodId, params }),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useEsmaReportReviewRules = ({ queryParams = {} } = {}) => {
  return useQuery(
    ['useEsmaReportReviewRules'],
    reportService.getEsmaReportReviewRules,
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetFCACredentials = ({ params = {}, queryParams = {}, workflowId } = {}) => {
  return useQuery(
    ['GET_FCA_CREDENTIALS', workflowId, params],
    () => reportService.getFCACredentials(workflowId, params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useUpdateData = ({ periodId, mutationSettings = {}, params = {} } = {}) => {
  return useMutation(data => reportService.updateData(data, periodId, params), mutationSettings);
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useAIFMUpdateData = ({ periodId, mutationSettings = {}, params = {} } = {}) => {
  return useMutation(
    data => reportService.updateAIFMData(data, periodId, params),
    mutationSettings
  );
};

export const usePatchOverrides = () => useMutation(reportService.patchOverrides);

export const useDeleteOverrides = () => useMutation(reportService.deleteOverrides);

export const useForceUpdateReport = () => {
  return useMutation(reportService.forceUpdateReport);
};

export const useInviteFromSettings = () => {
  return useMutation(reportService.inviteFromSettings);
};

export const useCreateAIFMReport = () => {
  return useMutation(reportService.createAIFMReport);
};

export const useRegenerateAIFMReport = () => {
  return useMutation(reportService.regenerateAIFMReport);
};

export const useGetDrillDown = () => {
  return useMutation(reportService.getDrillDown);
};

export const useInvite = () => {
  return useMutation(reportService.invite);
};

export const useApprovalRequiredAIF = () => {
  return useMutation(reportService.approvalRequiredAIF);
};

export const useApprovalRequiredAIFM = () => {
  return useMutation(reportService.approvalRequiredAIFM);
};

export const useAccessChangeFund = () => {
  return useMutation(reportService.accessFundChange);
};

export const useAccessChangeAllFunds = () => {
  return useMutation(reportService.accessAllFundsChange);
};

export const useInvitePending = () => {
  return useMutation(reportService.invitePending);
};
export const useResendInvite = () => {
  return useMutation(reportService.resendInvite);
};
export const useDeleteApprover = () => {
  return useMutation(reportService.deleteApprover);
};

export const useDeleteInvite = () => {
  return useMutation(reportService.deleteInvite);
};

export const useApprove = () => {
  return useMutation(reportService.approve);
};

export const useApproveAIFM = () => {
  return useMutation(reportService.approveAIFM);
};

export const useApproveAllFunds = () => {
  return useMutation(reportService.approveAllFunds);
};

export const useReplicateStaticData = () => {
  return useMutation(reportService.replicateStaticData);
};

export const useCreateFCACredentials = () => {
  return useMutation(reportService.createFCACredentials);
};

export const useCreateXML = () => {
  return useMutation(reportService.createXML);
};

export const useUpdateFCACredentials = () => {
  return useMutation(reportService.updateFCACredentials);
};

export const useDeleteFCACredentials = () => {
  return useMutation(reportService.deleteFCACredentials);
};

export const useDeleteStaticData = () => {
  return useMutation(reportService.deleteStaticData);
};

export const useSubmitReport = () => {
  return useMutation(reportService.submitReport);
};

export const useDeactivateUser = () => {
  return useMutation(reportService.deactivateUser);
};
